import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create context
const AuthContext = createContext(null);

// Role constants
export const UserRoles = {
  ADMIN: 'admin',
  EDITOR: 'editor',
  VIEWER: 'viewer'
};

// Permission checker
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  const rolePermissions = {
    admin: {
      canEditContent: true,
      canPublish: true,
      canManageUsers: true,
      canChangeSettings: true,
      canManageMedia: true,
    },
    editor: {
      canEditContent: true,
      canPublish: false, // Can be overridden
      canManageUsers: false,
      canChangeSettings: false,
      canManageMedia: true,
    },
    viewer: {
      canEditContent: false,
      canPublish: false,
      canManageUsers: false,
      canChangeSettings: false,
      canManageMedia: false,
    }
  };
  
  const perms = rolePermissions[user.role] || rolePermissions.viewer;
  
  // Special case: editors can have publish permission granted
  if (permission === 'canPublish' && user.role === 'editor') {
    return user.can_publish;
  }
  
  return perms[permission] || false;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get token from localStorage
  const getToken = useCallback(() => {
    return localStorage.getItem('cms_token');
  }, []);

  // Set axios default headers
  const setAuthHeader = useCallback((token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  // Fetch current user
  const fetchUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return null;
    }

    setAuthHeader(token);

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      // Token invalid or expired
      localStorage.removeItem('cms_token');
      setAuthHeader(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [getToken, setAuthHeader]);

  // Login
  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });

      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return {
          success: true,
          requires2FA: true,
          tempToken: response.data.temp_token,
          message: response.data.message
        };
      }

      const { access_token, user: userData, must_change_password } = response.data;
      
      // Store token
      localStorage.setItem('cms_token', access_token);
      setAuthHeader(access_token);
      setUser(userData);
      setError(null);

      return { 
        success: true, 
        requires2FA: false,
        user: userData,
        mustChangePassword: must_change_password 
      };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed. Please try again.';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Verify 2FA code and complete login
  const verify2FA = async (tempToken, code) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
        temp_token: tempToken,
        code
      });

      const { access_token, user: userData, must_change_password } = response.data;
      
      // Store token
      localStorage.setItem('cms_token', access_token);
      setAuthHeader(access_token);
      setUser(userData);
      setError(null);

      return { 
        success: true, 
        user: userData,
        mustChangePassword: must_change_password 
      };
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid verification code.';
      setError(message);
      return { success: false, error: message };
    }
  };

  // Logout
  const logout = useCallback(() => {
    localStorage.removeItem('cms_token');
    setAuthHeader(null);
    setUser(null);
    setError(null);
  }, [setAuthHeader]);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    return await fetchUser();
  }, [fetchUser]);

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to change password.';
      return { success: false, error: message };
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      return { success: true, message: response.data.message };
    } catch (err) {
      // Always return success to prevent email enumeration
      return { success: true, message: 'If an account exists with that email, a password reset link will be sent.' };
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to reset password.';
      return { success: false, error: message };
    }
  };

  // Check if user has a specific role
  const isRole = useCallback((role) => {
    return user?.role === role;
  }, [user]);

  // Check if user is admin
  const isAdmin = useCallback(() => {
    return user?.role === UserRoles.ADMIN;
  }, [user]);

  // Check if user is editor or admin
  const canEdit = useCallback(() => {
    return user?.role === UserRoles.ADMIN || user?.role === UserRoles.EDITOR;
  }, [user]);

  // Check if user can publish
  const canPublishContent = useCallback(() => {
    if (!user) return false;
    if (user.role === UserRoles.ADMIN) return true;
    if (user.role === UserRoles.EDITOR) return user.can_publish;
    return false;
  }, [user]);

  // Check if user can manage settings
  const canManageSettings = useCallback(() => {
    return user?.role === UserRoles.ADMIN;
  }, [user]);

  // Initialize auth state
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value = {
    user,
    loading,
    error,
    login,
    verify2FA,
    logout,
    changePassword,
    forgotPassword,
    resetPassword,
    fetchUser,
    refreshUser,
    isRole,
    isAdmin,
    canEdit,
    canPublishContent,
    canManageSettings,
    hasPermission: (permission) => hasPermission(user, permission),
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
